/**
 * usePlayback — subscribes to the backend sim playback clock and exposes
 * imperative controls that map to POST /api/sim/playback/*.
 *
 * Phase 14b. Polls at 1s cadence (matches the badge display resolution).
 * Every control call posts, then merges the returned state into local
 * state immediately for instant UI feedback (backend is authoritative
 * on the very next tick).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getBaseUrl } from "../lib/api";
const BASE = () => getBaseUrl();
async function postJson(path, body) {
    const res = await fetch(`${BASE()}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok)
        throw new Error(`${path} → HTTP ${res.status}`);
    return (await res.json());
}
export function usePlayback(pollIntervalMs = 1000) {
    const [state, setState] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const cancelledRef = useRef(false);
    // Passive poll to keep the time badge fresh.
    useEffect(() => {
        cancelledRef.current = false;
        const load = () => fetch(`${BASE()}/api/sim/playback`)
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => {
            if (cancelledRef.current || !j)
                return;
            setState(j);
            setLoading(false);
            setError(null);
        })
            .catch((e) => {
            if (cancelledRef.current)
                return;
            setError(String(e));
            setLoading(false);
        });
        void load();
        const t = window.setInterval(load, pollIntervalMs);
        return () => {
            cancelledRef.current = true;
            window.clearInterval(t);
        };
    }, [pollIntervalMs]);
    const apply = useCallback((s) => setState(s), []);
    const play = useCallback(async () => {
        apply(await postJson("/api/sim/playback/play"));
    }, [apply]);
    const pause = useCallback(async () => {
        apply(await postJson("/api/sim/playback/pause"));
    }, [apply]);
    const playPause = useCallback(async () => {
        if (state?.paused)
            return play();
        return pause();
    }, [state, play, pause]);
    const step = useCallback(async (delta_s) => {
        apply(await postJson("/api/sim/playback/step", { delta_s }));
    }, [apply]);
    const setSpeed = useCallback(async (speed) => {
        apply(await postJson("/api/sim/playback", { speed }));
    }, [apply]);
    const seek = useCallback(async (sim_ts) => {
        apply(await postJson("/api/sim/playback", {
            seek_sim_ts: sim_ts,
        }));
    }, [apply]);
    const reset = useCallback(async () => {
        apply(await postJson("/api/sim/playback/reset"));
    }, [apply]);
    return useMemo(() => ({
        state,
        loading,
        error,
        playPause,
        play,
        pause,
        step,
        setSpeed,
        seek,
        reset,
    }), [state, loading, error, playPause, play, pause, step, setSpeed, seek, reset]);
}
